import { Component, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TeamService, TeamItem } from '../../../services/team/team.service';
import { TeamModalComponent } from './team-modal/team-modal.component';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from "../../layout/spinner/spinner.component";
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LanguageService } from '../../../services/language/language.service';

@Component({
  selector: 'app-team',
  imports: [DragDropModule, CommonModule, SpinnerComponent, FormsModule],
  templateUrl: './team.component.html',
  styleUrl: './team.component.css'
})
export class TeamComponent implements OnDestroy {
  @ViewChild('scrollTarget') scrollTarget!: ElementRef;

  currentLang: 'geo' | 'eng' = 'geo';
  visibleFilter: 'all' | 0 | 1 = 'all';
  search = '';
  loading = false;

  items: TeamItem[] = [];
  togglingId: number | null = null;

  private destroy$ = new Subject<void>();

  private searchTimer: any = null;

  constructor(
    private teamService: TeamService,
    private modalService: NgbModal,
    private langService: LanguageService,
  ) {
    // initial language from your language service
    this.currentLang = this.langService.getLanguage() as any;

    // react to language switch
    this.langService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: any) => {
        this.currentLang = lang;
        this.get();
      });
  }

  ngOnInit() {
    this.get();
  }

  get() {
    this.loading = true;
    this.teamService
      .list({ lang: this.currentLang, search: this.search, visible: this.visibleFilter })
      .subscribe({
        next: (res) => {
          this.items = res?.data || [];
          this.loading = false;
          this.scrollToTop();
        },
        error: () => (this.loading = false)
      });
  }

  onSearchChange() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.get(), 350);
  }

  drop(event: CdkDragDrop<TeamItem[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);

    // backend-ში ვგზავნით ids რიგითობას
    const ids = this.items.map(x => x.id);

    // სურვილისამებრ: optimistic UI — უკვე გადალაგებულია
    this.teamService.sort(this.currentLang, ids).subscribe({
      next: () => {},
      error: () => {
        // თუ sort ვერ შენახა, უბრალოდ ხელახლა წამოიღე
        this.get();
      }
    });
  }

  openCreate() {
    const modalRef = this.modalService.open(TeamModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.lang = this.currentLang;

    modalRef.closed.subscribe((saved: boolean) => {
      if (saved) this.get();
    });
  }

  openEdit(item: TeamItem) {
    const modalRef = this.modalService.open(TeamModalComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.lang = this.currentLang;
    modalRef.componentInstance.itemId = item.id;

    modalRef.closed.subscribe((saved: boolean) => {
      if (saved) this.get();
    });
  }

toggleVisible(item: TeamItem) {
  this.togglingId = item.id;

  this.teamService.toggleVisible(item.id, this.currentLang).subscribe({
    next: (res) => {
      item.visible = res?.visible ?? item.visible;
      this.togglingId = null;
    },
    error: () => {
      this.togglingId = null;
    }
  });
}

  remove(item: TeamItem) {
    if (!confirm(`წავშალო: ${item.name}?`)) return;

    this.teamService.delete(item.id, this.currentLang).subscribe({
      next: () => this.get()
    });
  }

  getFullImageUrl(imagePath: string): string {
    if (!imagePath) return '';

    const raw = String(imagePath).trim();
    if (!raw) return '';

    // Old absolute URLs like https://api.sca.ge/storage/res/...
    // should now be served through Laravel route: /api/media/...
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      try {
        const url = new URL(raw);
        if (url.hostname === 'api.sca.ge' && url.pathname.startsWith('/storage/')) {
          const cleanPath = url.pathname.replace(/^\/storage\//, '');
          return `https://api.sca.ge/api/media/${cleanPath}`;
        }
        return raw;
      } catch {
        return raw;
      }
    }

    // Relative path like res/list/2026/03/file.webp
    const cleanPath = raw.replace(/^\/+/, '');
    return `https://api.sca.ge/api/media/${cleanPath}`;
  }


  private scrollToTop() {
    try {
      this.scrollTarget?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
